import React, { useMemo } from "react";
import { Alert, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../Context/AppContext";
import { useGetUser } from "../../api/queries/useGetUser";
import { isLoggedIn } from "../../auth/authStorage";
import {
  isEarlyStageMembership,
  isLegacyYouthMembership,
  isPaidPortalMember,
  isYouthOrEarlyStageMembership,
} from "../../utils/membershipTier";

/**
 * Incomplete-profile alert. Same visibility rules as the former
 * dashboard-only banner — mount at the top of each page that should show it.
 * Hidden on the registration / JazakAllah page only.
 */
const CompleteProfileBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { objectId } = useAppContext();

  const { data: userData } = useGetUser({
    id: objectId || "",
    enabled: isLoggedIn() && !!objectId,
  });

  const showCompleteProfileBanner = useMemo(() => {
    // Hide only on registration confirmation (JazakAllah); show elsewhere when incomplete.
    if (pathname === "/register-user") return false;
    const u = userData?.user;
    if (!u) return false;
    if ((u.hasCompletedProfile ?? 0) === 1) return false;
    if (!u.membershipName?.trim()) return false;
    // Explorers / unpaid users skip this banner until they have an active paid membership.
    if (!isPaidPortalMember(u)) return false;

    // Renew / Upgrade flow already handles canceled or expired membership.
    const stripeStatus = u.stripeStatus?.trim().toLowerCase() ?? "";
    if (
      stripeStatus === "canceled" ||
      stripeStatus === "cancelled" ||
      stripeStatus === "incomplete_expired"
    ) {
      return false;
    }

    if (isEarlyStageMembership(u.membershipName)) {
      return true;
    }

    if (isLegacyYouthMembership(u.membershipName)) {
      return !(u.gender?.trim() && u.mentorshipFormat?.trim());
    }

    return !(
      u.personalGender?.trim() &&
      u.businessAddressLineOne?.trim() &&
      u.productInterested?.trim()
    );
  }, [pathname, userData?.user]);

  const handleCompleteProfile = () => {
    if (isYouthOrEarlyStageMembership(userData?.user?.membershipName)) {
      navigate("/youth-membership-form");
    } else {
      navigate("/business-membership-form");
    }
  };

  if (!showCompleteProfileBanner) {
    return null;
  }

  // Layout clears the fixed navbar; only a small gap under the alert before page content.
  return (
    <div className="mb-2">
      <Alert
        variant="danger"
        className="mb-0 w-100 p-3 shadow-sm d-flex flex-column flex-md-row justify-content-between align-items-center gap-2"
      >
        <div className="text-center text-md-start">
          {t("dashboard.profileIncomplete")}
        </div>
        <Button variant="primary" className="px-4 text-nowrap" onClick={handleCompleteProfile}>
          {t("dashboard.completeProfile")}
        </Button>
      </Alert>
    </div>
  );
};

CompleteProfileBanner.displayName = "CompleteProfileBanner";

export default CompleteProfileBanner;
